import {
  CourseModernizerIntakeStatus,
  ExternalCourseListSourceType,
  ExternalCourseModernizationStatus,
  ModernizedAuditStatus,
  type ExternalCourse,
  type ExternalCourseModernizerArchitect,
  type ListExternalCoursesQuery,
} from "@axio/graphql/generated/graphql";
import { hasMinRole, UserRoleUI } from "../../../constants/rolesAndPermissions";
import type {
  CourseModernizerCourseRow,
  CourseModernizerRowAction,
  CourseModernizerRowArchitect,
  CourseModernizerRowStatusTone,
} from "../../../components/adminDashboard/CourseModernizer/CourseModernizerCoursesTable";

const ACTIVE_MODERNIZATION_STATUSES = new Set<ModernizedAuditStatus>([
  ModernizedAuditStatus.NotStarted,
  ModernizedAuditStatus.InProgress,
  ModernizedAuditStatus.Modernized,
]);

const SOURCE_LINKED_STATUS_LABELS: Record<ExternalCourseModernizationStatus, string> = {
  [ExternalCourseModernizationStatus.NotStarted]: "Not Started",
  [ExternalCourseModernizationStatus.Invited]: "Invited",
  [ExternalCourseModernizationStatus.IntakeReady]: "Outcomes Approved",
  [ExternalCourseModernizationStatus.IntakeInProgress]: "Intake In Progress",
  [ExternalCourseModernizationStatus.ReadyToEvaluate]: "Ready to Evaluate",
  [ExternalCourseModernizationStatus.Evaluating]: "Evaluating",
  [ExternalCourseModernizationStatus.ReadyForReview]: "Ready for Review",
  [ExternalCourseModernizationStatus.Published]: "Modernized",
  [ExternalCourseModernizationStatus.InProgress]: "In Progress",
  [ExternalCourseModernizationStatus.Modernized]: "Modernized",
};

const INTAKE_STATUS_LABELS: Record<CourseModernizerIntakeStatus, string> = {
  [CourseModernizerIntakeStatus.NotStarted]: "Not Started",
  [CourseModernizerIntakeStatus.Invited]: "Invited",
  [CourseModernizerIntakeStatus.IntakeReady]: "Outcomes Approved",
  [CourseModernizerIntakeStatus.IntakeInProgress]: "Intake In Progress",
  [CourseModernizerIntakeStatus.ReadyToEvaluate]: "Ready to Evaluate",
  [CourseModernizerIntakeStatus.Evaluating]: "Evaluating",
  [CourseModernizerIntakeStatus.ReadyForReview]: "Ready for Review",
  [CourseModernizerIntakeStatus.Published]: "Modernized",
};

const SOURCE_LINKED_STATUS_TONES: Record<ExternalCourseModernizationStatus, CourseModernizerRowStatusTone> = {
  [ExternalCourseModernizationStatus.NotStarted]: "notStarted",
  [ExternalCourseModernizationStatus.Invited]: "default",
  [ExternalCourseModernizationStatus.IntakeReady]: "default",
  [ExternalCourseModernizationStatus.IntakeInProgress]: "inProgress",
  [ExternalCourseModernizationStatus.ReadyToEvaluate]: "default",
  [ExternalCourseModernizationStatus.Evaluating]: "inProgress",
  [ExternalCourseModernizationStatus.ReadyForReview]: "modernized",
  [ExternalCourseModernizationStatus.Published]: "modernized",
  [ExternalCourseModernizationStatus.InProgress]: "inProgress",
  [ExternalCourseModernizationStatus.Modernized]: "modernized",
};

const INTAKE_STATUS_TONES: Record<CourseModernizerIntakeStatus, CourseModernizerRowStatusTone> = {
  [CourseModernizerIntakeStatus.NotStarted]: "notStarted",
  [CourseModernizerIntakeStatus.Invited]: "default",
  [CourseModernizerIntakeStatus.IntakeReady]: "default",
  [CourseModernizerIntakeStatus.IntakeInProgress]: "inProgress",
  [CourseModernizerIntakeStatus.ReadyToEvaluate]: "default",
  [CourseModernizerIntakeStatus.Evaluating]: "inProgress",
  [CourseModernizerIntakeStatus.ReadyForReview]: "modernized",
  [CourseModernizerIntakeStatus.Published]: "modernized",
};

type CourseModernizerRowOptions = {
  currentUserId?: string | null;
  lmsDirectFacultyMode?: boolean | null;
  userRole?: UserRoleUI | null;
};

export type CourseModernizerSourceHints = {
  termLabel: string | null;
  lengthInWeeks: number | null;
};

type ListExternalCourse = NonNullable<
  NonNullable<NonNullable<ListExternalCoursesQuery["listExternalCourses"]>["data"]>["courses"]
>[number];

export const getTeachingFacultySummary = (course: Pick<ListExternalCourse, "teachingFaculty">): string | null =>
  course.teachingFaculty
    ?.map((faculty) => faculty?.name)
    .filter(Boolean)
    .join(", ") || null;

const parseJsonObject = (value: unknown): Record<string, unknown> | null => {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }

      return null;
    } catch {
      return null;
    }
  }

  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return null;
};

const toOptionalTrimmedString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const toPositiveNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
};

export const getCourseModernizerSourceHints = (course: ExternalCourse): CourseModernizerSourceHints => {
  const sourceMeta = parseJsonObject(course.sourceMeta);
  const sourceHints = parseJsonObject(sourceMeta?.modernizerHints);

  return {
    termLabel: toOptionalTrimmedString(sourceHints?.termLabel),
    lengthInWeeks: toPositiveNumber(sourceHints?.lengthInWeeks),
  };
};

const getModernizedCourseRank = (status: ModernizedAuditStatus | null | undefined): number => {
  if (!status) {
    return -1;
  }

  switch (status) {
    case ModernizedAuditStatus.InProgress:
      return 3;
    case ModernizedAuditStatus.Modernized:
      return 2;
    case ModernizedAuditStatus.NotStarted:
      return 1;
    default:
      return -1;
  }
};

const getModernizedCourseTimestamp = (created?: string | null, lastModified?: string | null): number => {
  const timestamp = lastModified ?? created;
  return timestamp ? new Date(timestamp).getTime() : 0;
};

export const getActiveModernizedCourse = (course: ExternalCourse) => {
  const candidates = (course.modernizedCourses ?? [])
    .filter((modernizedCourse): modernizedCourse is NonNullable<typeof modernizedCourse> => Boolean(modernizedCourse))
    .filter(
      (modernizedCourse) =>
        Boolean(modernizedCourse.status) &&
        ACTIVE_MODERNIZATION_STATUSES.has(modernizedCourse.status as ModernizedAuditStatus)
    )
    .sort((left, right) => {
      const rankDifference = getModernizedCourseRank(right.status) - getModernizedCourseRank(left.status);
      if (rankDifference !== 0) {
        return rankDifference;
      }

      return (
        getModernizedCourseTimestamp(right.created ?? null, right.lastModified ?? null) -
        getModernizedCourseTimestamp(left.created ?? null, left.lastModified ?? null)
      );
    });

  return candidates[0] ?? null;
};

export const getCourseTermLabel = (course: ExternalCourse, selectedTerm: string | null | undefined): string | null => {
  if (selectedTerm !== undefined) {
    return selectedTerm;
  }

  const hints = getCourseModernizerSourceHints(course);
  return course.term?.label ?? hints.termLabel ?? null;
};

export const getCourseRowFromExternalCourse = (
  course: ExternalCourse,
  selectedTerm?: string | null,
  options: CourseModernizerRowOptions = {}
): CourseModernizerCourseRow => {
  const activeModernizedCourse = getActiveModernizedCourse(course);
  const termLabel = getCourseTermLabel(course, selectedTerm);
  const isUploadedDocumentCourse =
    course.sourceType === ExternalCourseListSourceType.Pdf || course.sourceType === ExternalCourseListSourceType.Docx;

  const modernizerIntakeSummary = course.modernizerIntakeSummary ?? null;
  const architect = getArchitectDisplayModel(modernizerIntakeSummary?.architect ?? null);
  const isSchoolAdmin = Boolean(options.userRole && hasMinRole(options.userRole, UserRoleUI.SCHOOL_ADMIN));
  const hasModernizerIntake = Boolean(modernizerIntakeSummary?.id);
  const isCurrentUserArchitect = isAssignedCourseModernizerArchitect({
    architect,
    currentUserId: options.currentUserId,
  });
  const isDirectFacultyForCourse = isDirectFacultyCourseModernizerUser({
    course,
    currentUserId: options.currentUserId,
    lmsDirectFacultyMode: options.lmsDirectFacultyMode,
    userRole: options.userRole,
  });
  const hasAdminLikeCourseAccess = isSchoolAdmin || isDirectFacultyForCourse;
  const canManageSourceWorkflow = canManageCourseModernizerSourceWorkflow({
    architect,
    currentUserId: options.currentUserId,
    isDirectFacultyForCourse,
    userRole: options.userRole,
  });
  const hasCompletedModernization = (course.modernizedCourses ?? []).some(
    (modernizedCourse) => modernizedCourse?.status === ModernizedAuditStatus.Modernized
  );
  const sourceLinkedStatus = course.modernizationStatus ?? ExternalCourseModernizationStatus.NotStarted;
  const intakeStatus = modernizerIntakeSummary?.intakeStatus ?? null;
  const sourceStatusLabel = intakeStatus
    ? INTAKE_STATUS_LABELS[intakeStatus]
    : SOURCE_LINKED_STATUS_LABELS[sourceLinkedStatus];
  const statusTone = intakeStatus ? INTAKE_STATUS_TONES[intakeStatus] : SOURCE_LINKED_STATUS_TONES[sourceLinkedStatus];
  const canOpenIntakeWizard = isCurrentUserArchitect || hasAdminLikeCourseAccess;
  const shellSettingsAction: CourseModernizerRowAction[] =
    hasAdminLikeCourseAccess && hasModernizerIntake && !isUploadedDocumentCourse
      ? [{ id: "editShell" as const, label: "Shell settings", requiresReadyRubric: true }]
      : [];
  const canResetEvaluatingIntake = hasAdminLikeCourseAccess || isCurrentUserArchitect;
  const evaluatingSecondaryActions: CourseModernizerRowAction[] | null =
    canResetEvaluatingIntake && intakeStatus === CourseModernizerIntakeStatus.Evaluating && !isUploadedDocumentCourse
      ? [...shellSettingsAction, { id: "reset" as const, label: "Reset" }]
      : null;
  const canResetPostModernizationIntake =
    canResetEvaluatingIntake &&
    hasModernizerIntake &&
    !isUploadedDocumentCourse &&
    (intakeStatus === CourseModernizerIntakeStatus.ReadyForReview ||
      intakeStatus === CourseModernizerIntakeStatus.Published);

  if (evaluatingSecondaryActions) {
    return {
      course,
      termLabel,
      architect,
      statusLabel: sourceStatusLabel,
      statusTone,
      primaryAction: null,
      secondaryActions: evaluatingSecondaryActions,
      activeModernizedCourseId: activeModernizedCourse?.id ?? null,
      modernizerIntakeId: modernizerIntakeSummary?.id ?? null,
    };
  }

  if (modernizerIntakeSummary) {
    const primaryAction = canManageSourceWorkflow
      ? getIntakePrimaryAction(modernizerIntakeSummary.intakeStatus, {
          canOpenIntakeWizard,
          canOpenShellSettings: hasAdminLikeCourseAccess,
          isUploadedDocumentCourse,
        })
      : null;

    if (primaryAction) {
      return {
        course,
        termLabel,
        architect,
        statusLabel: sourceStatusLabel,
        statusTone,
        primaryAction,
        secondaryActions: shellSettingsAction.filter((action) => action.id !== primaryAction.id),
        activeModernizedCourseId: activeModernizedCourse?.id ?? null,
        modernizerIntakeId: modernizerIntakeSummary.id,
      };
    }
  }

  if (!activeModernizedCourse) {
    const sourceLinkedSecondaryActions: CourseModernizerRowAction[] = hasCompletedModernization
      ? [...shellSettingsAction, { id: "rerun" as const, label: "Re-run", requiresReadyRubric: true }]
      : shellSettingsAction;
    const secondaryActions = sourceLinkedSecondaryActions;

    if (modernizerIntakeSummary) {
      const primaryAction = canManageSourceWorkflow
        ? getIntakePrimaryAction(modernizerIntakeSummary.intakeStatus, {
            canOpenIntakeWizard,
            canOpenShellSettings: hasAdminLikeCourseAccess,
            isUploadedDocumentCourse,
          })
        : null;

      return {
        course,
        termLabel,
        architect,
        statusLabel: sourceStatusLabel,
        statusTone,
        primaryAction,
        secondaryActions:
          primaryAction?.id === "editShell"
            ? secondaryActions.filter((action) => action.id !== "editShell")
            : secondaryActions,
        activeModernizedCourseId: null,
        modernizerIntakeId: modernizerIntakeSummary?.id ?? null,
      };
    }

    return {
      course,
      termLabel,
      architect,
      statusLabel: SOURCE_LINKED_STATUS_LABELS[sourceLinkedStatus],
      statusTone,
      primaryAction: getSourceLinkedPrimaryAction(sourceLinkedStatus, {
        canStart: hasAdminLikeCourseAccess,
        isUploadedDocumentCourse,
      }),
      secondaryActions: hasCompletedModernization ? [{ id: "rerun", label: "Re-run", requiresReadyRubric: true }] : [],
      activeModernizedCourseId: null,
      modernizerIntakeId: null,
    };
  }

  if (activeModernizedCourse.status === ModernizedAuditStatus.NotStarted) {
    return {
      course,
      termLabel,
      architect,
      statusLabel: sourceStatusLabel,
      statusTone,
      primaryAction: null,
      secondaryActions: [
        ...shellSettingsAction,
        ...(hasCompletedModernization ? [{ id: "rerun" as const, label: "Re-run", requiresReadyRubric: true }] : []),
      ],
      activeModernizedCourseId: activeModernizedCourse.id,
      modernizerIntakeId: modernizerIntakeSummary?.id ?? null,
    };
  }

  // Recovery for reset rows that lost the intake before their generated course was archived.
  if (!modernizerIntakeSummary && sourceLinkedStatus === ExternalCourseModernizationStatus.InProgress) {
    return {
      course,
      termLabel,
      architect,
      statusLabel: sourceStatusLabel,
      statusTone,
      primaryAction: hasAdminLikeCourseAccess ? { id: "start", label: "Start", requiresReadyRubric: true } : null,
      secondaryActions: [],
      activeModernizedCourseId: activeModernizedCourse.id,
      modernizerIntakeId: null,
    };
  }

  const isReviewReadyFromSourceStatus =
    sourceLinkedStatus === ExternalCourseModernizationStatus.ReadyForReview ||
    sourceLinkedStatus === ExternalCourseModernizationStatus.Published ||
    sourceLinkedStatus === ExternalCourseModernizationStatus.Modernized;
  const isReviewReadyFromIntakeStatus =
    intakeStatus === CourseModernizerIntakeStatus.ReadyForReview ||
    intakeStatus === CourseModernizerIntakeStatus.Published;
  const canViewResults = Boolean(
    activeModernizedCourse.id && (isReviewReadyFromSourceStatus || isReviewReadyFromIntakeStatus)
  );
  const canRerun = hasAdminLikeCourseAccess && (isReviewReadyFromSourceStatus || isReviewReadyFromIntakeStatus);
  const secondaryActions: CourseModernizerRowAction[] = [
    ...shellSettingsAction,
    ...(canResetPostModernizationIntake ? [{ id: "reset" as const, label: "Reset" }] : []),
    ...(canRerun ? [{ id: "rerun" as const, label: "Re-run", requiresReadyRubric: true }] : []),
    ...(canViewResults ? [{ id: "viewResults" as const, label: "View Results" }] : []),
  ];

  return {
    course,
    termLabel,
    architect,
    statusLabel: sourceStatusLabel,
    statusTone,
    primaryAction: null,
    secondaryActions,
    activeModernizedCourseId: activeModernizedCourse.id,
    modernizerIntakeId: modernizerIntakeSummary?.id ?? null,
  };
};

const canManageCourseModernizerSourceWorkflow = ({
  architect,
  currentUserId,
  isDirectFacultyForCourse,
  userRole,
}: {
  architect: CourseModernizerRowArchitect | null;
  currentUserId?: string | null;
  isDirectFacultyForCourse?: boolean;
  userRole?: UserRoleUI | null;
}): boolean => {
  return (
    isDirectFacultyForCourse === true ||
    isAssignedCourseModernizerArchitect({ architect, currentUserId }) ||
    Boolean(userRole && hasMinRole(userRole, UserRoleUI.SCHOOL_ADMIN))
  );
};

export const isDirectFacultyCourseModernizerUser = ({
  course,
  currentUserId,
  lmsDirectFacultyMode,
  userRole,
}: {
  course: ExternalCourse;
  currentUserId?: string | null;
  lmsDirectFacultyMode?: boolean | null;
  userRole?: UserRoleUI | null;
}): boolean => {
  return Boolean(
    lmsDirectFacultyMode === true &&
      userRole === UserRoleUI.FACULTY &&
      currentUserId &&
      course.teachingFaculty?.some((faculty) => faculty?.id === currentUserId)
  );
};

const isAssignedCourseModernizerArchitect = ({
  architect,
  currentUserId,
}: {
  architect: CourseModernizerRowArchitect | null;
  currentUserId?: string | null;
}): boolean => {
  return Boolean(currentUserId && architect?.id === currentUserId);
};

const getArchitectDisplayModel = (
  architect?: ExternalCourseModernizerArchitect | null
): CourseModernizerRowArchitect | null => {
  if (!architect?.id) {
    return null;
  }

  const name = architect.name?.trim() || architect.email?.trim() || "Unnamed architect";

  return {
    id: architect.id,
    name,
    email: architect.email ?? null,
    profilePicture: architect.profilePicture ?? null,
  };
};

const getIntakePrimaryAction = (
  intakeStatus: CourseModernizerIntakeStatus,
  options: { canOpenIntakeWizard?: boolean; canOpenShellSettings?: boolean; isUploadedDocumentCourse?: boolean } = {}
): CourseModernizerRowAction | null => {
  switch (intakeStatus) {
    case CourseModernizerIntakeStatus.NotStarted:
      if (options.isUploadedDocumentCourse && options.canOpenIntakeWizard) {
        return { id: "openWizard", label: "Intake Wizard" };
      }
      return options.canOpenShellSettings
        ? { id: "editShell", label: "Shell settings", requiresReadyRubric: true }
        : null;
    case CourseModernizerIntakeStatus.Invited:
      if (options.canOpenIntakeWizard) {
        return { id: "openWizard", label: "Intake Wizard" };
      }
      return options.canOpenShellSettings
        ? { id: "editShell", label: "Shell settings", requiresReadyRubric: true }
        : null;
    case CourseModernizerIntakeStatus.IntakeInProgress:
      return options.canOpenIntakeWizard ? { id: "openWizard", label: "In progress" } : null;
    case CourseModernizerIntakeStatus.IntakeReady:
      return options.canOpenIntakeWizard ? { id: "openWizard", label: "Intake Wizard" } : null;
    case CourseModernizerIntakeStatus.ReadyToEvaluate:
      return { id: "evaluate", label: "Evaluate/Modernize", requiresReadyRubric: true };
    case CourseModernizerIntakeStatus.Evaluating:
      if (options.isUploadedDocumentCourse) {
        return { id: "evaluate", label: "Evaluate/Modernize", requiresReadyRubric: true };
      }
      return null;
    case CourseModernizerIntakeStatus.ReadyForReview:
    case CourseModernizerIntakeStatus.Published:
      return null;
    default:
      return null;
  }
};

const getSourceLinkedPrimaryAction = (
  sourceLinkedStatus: ExternalCourseModernizationStatus,
  options: { canStart?: boolean; isUploadedDocumentCourse?: boolean } = {}
): CourseModernizerRowAction | null => {
  switch (sourceLinkedStatus) {
    case ExternalCourseModernizationStatus.NotStarted:
      return options.canStart ? { id: "start", label: "Start", requiresReadyRubric: true } : null;
    case ExternalCourseModernizationStatus.ReadyToEvaluate:
      return { id: "evaluate", label: "Evaluate/Modernize", requiresReadyRubric: true };
    case ExternalCourseModernizationStatus.Evaluating:
      if (options.isUploadedDocumentCourse) {
        return { id: "evaluate", label: "Evaluate/Modernize", requiresReadyRubric: true };
      }
      return null;
    default:
      return null;
  }
};
